import { createTheme } from '@mui/material/styles';

const color = {
  primary: {
    w0: '#e9efee',
    w50: '#e0f4f2',
    w100: '#b2e4dd',
    w200: '#7ed3c8',
    w300: '#45c1b2',
    w400: '#00b2a0',
    w500: '#00a38f',
    w600: '#009681',
    w700: '#008571',
    w800: '#007562',
    w900: '#005845'
  },
  grey: {
    w0: '#F9FAFC',
    w50: '#F4F6FA',
    w100: '#E6ECF2',
    w200: '#C3CED9',
    w300: '#A9B7C4',
    w400: '#9AA9B8',
    w500: '#666666',
    w600: '#5C6B7F',
    w700: '#42526B',
    w800: '#2D3C57',
    w900: '#222E4A'
  },
  blue: {
    w0: '#F4FBFF',
    w50: '#e3f2fa',
    w100: '#bbdff5',
    w200: '#92ccef',
    w300: '#68b8e9',
    w400: '#48a9e6',
    w500: '#279be2',
    w600: '#1c8dd5',
    w700: '#0d7bc3',
    w800: '#006bb2',
    w900: '#004e93'
  },
  orange: {
    w0: '#f3f1f0',
    w50: '#faeae6',
    w100: '#fbceba',
    w200: '#f8b08d',
    w300: '#f5925f',
    w400: '#f27c3b',
    w500: '#f06811',
    w600: '#e6620c',
    w700: '#d85b07',
    w800: '#cb5404',
    w900: '#b24700'
  },
  purple: {
    w0: '#fffee8',
    w50: '#faeae6',
    w100: '#fbceba',
    w200: '#f8b08d',
    w300: '#f5925f',
    w400: '#f27c3b',
    w500: '#f06811',
    w600: '#e6620c',
    w700: '#4526c3',
    w800: '#3420bd',
    w900: '#0012b2'
  },
  red: {
    main: '#FF3552',
    light: '#FFD4DA',
  },
  lightblue: {
    main: '#33C2FF',
    light: '#E2F6FF'
  },
  text: '#444444',
  title: '#11192A',
}

const fontSize = {
  lg: '1.25rem',
  md: '1.125rem',
  sm: '0.875rem',
  xs: '0.75rem'
};

const theme = createTheme({
  palette: {
    background: {
      default: '#FDFDFD',
      paper: '#ffffff'
    },
    primary: {
      main: color.primary.w600,
      light: color.primary.w300,
      dark: color.primary.w800,
      w0: color.primary.w0,
      w50: color.primary.w50,
      w100: color.primary.w100,
      w200: color.primary.w200,
      w300: color.primary.w300,
      w400: color.primary.w400,
      w500: color.primary.w500,
      w600: color.primary.w600,
      w700: color.primary.w700,
      w800: color.primary.w800,
      w900: color.primary.w900
    },
    secondary: {
      main: color.blue.w600,
      light: color.blue.w300,
      dark: color.blue.w800,
      w0: color.blue.w0,
      w50: color.blue.w50,
      w100: color.blue.w100,
      w200: color.blue.w200,
      w300: color.blue.w300,
      w400: color.blue.w400,
      w500: color.blue.w500,
      w600: color.blue.w600,
      w700: color.blue.w700,
      w800: color.blue.w800,
      w900: color.blue.w900
    },
    error: {
      main: color.red.main,
      light: color.red.light
    },
    warning: {
      main: color.orange.w700,
      light: color.orange.w50
    },
    success: {
      main: color.primary.w400,
      light: color.primary.w100
    },
    lightblue: {
      main: color.lightblue.main,
      light: color.lightblue.light
    },
    text: {
      primary: color.text,
      disabled: color.grey.w400
    },
    greyOutlined: {
      main: color.grey.w300
    },
    grey: {
      main: color.grey.w600,
      light: color.grey.w300,
      dark: color.grey.w800,
      w0: color.grey.w0,
      w50: color.grey.w50,
      w100: color.grey.w100,
      w200: color.grey.w200,
      w300: color.grey.w300,
      w400: color.grey.w400,
      w500: color.grey.w500,
      w600: color.grey.w600,
      w700: color.grey.w700,
      w800: color.grey.w800,
      w900: color.grey.w900
    },
    title: {
      main: color.title
    }
  },
  shadows: [
    "none",
    "0px 0px 16px rgba(107, 117, 128, 0.15)",
    "0px 0px 16px rgba(107, 117, 128, 1)",
    "0px 0px 16px rgba(107, 117, 128, 1)",
    "0px 0px 16px rgba(107, 117, 128, 1)",
    "0px 0px 16px rgba(107, 117, 128, 1)",
    "0px 0px 16px rgba(107, 117, 128, 1)",
    "0px 0px 16px rgba(107, 117, 128, 1)",
    "0px 0px 16px rgba(107, 117, 128, 0.3)",
    ...Array(17).fill('none')
  ],
  typography: {
    fontFamily: '"Open sans", "Noto Sans TC", "sans-serif"',
    h1: {
      fontSize: '2rem',
      fontWeight: 600,
    },
    h2: {
      fontSize: '1.5rem',
      fontWeight: 600,
    },
    h3: {
      fontSize: '1.25rem',
      fontWeight: 500,
    },
    h4: {
      fontSize: '1.125rem',
      fontWeight: 400,
    },
    h5: {
      fontSize: '1rem',
      fontWeight: 400,
    },
    h6: {
      fontSize: '0.875rem',
      fontWeight: 400,
    },
    subtitle1: {
      fontWeight: 400,
      lineHeight: 1.5
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 400,
    },
    body1: {
      fontWeight: 400,
    },
    body2: {
      fontWeight: 400,
    },
    body_lg: {
      fontSize: '1.25rem'
    },
    body_base: {
      fontFamily: '"Open sans", "Noto Sans TC", "sans-serif"',
    },
    body_md: {
      fontSize: '1.125rem'
    },
    body_sm: {
      fontSize: '0.875rem'
    },
    body_xs: {
      fontSize: '0.75rem'
    },
    button: {
      fontSize: '1rem'
    },
    caption: {
      fontSize: '0.75rem'
    },
    overline: {
      fontFamily: '"Open sans", "Noto Sans TC", "sans-serif"',
    },
  },
  borderRadius: {
    small: '4px',
    normal: '8px',
    md: '16px'
  },
  customFontSize: {
    lg: fontSize.lg,
    md: fontSize.md,
    sm: fontSize.sm,
    xs: fontSize.xs
  },
  components: {
    MuiStack: {
      defaultProps: {
        direction: 'row',
        flexWrap: 'wrap',
      },
      variants: [
        {
          props: { variant: 'center-center' },
          style: {
            justifyContent: 'center',
            alignItems: 'center'
          },
        },
        {
          props: { variant: 'start-center' },
          style: {
            justifyContent: 'flex-start',
            alignItems: 'center'
          },
        },
        {
          props: { variant: 'end-center' },
          style: {
            justifyContent: 'flex-end',
            alignItems: 'center'
          },
        },
        {
          props: { variant: 'arround-center' },
          style: {
            justifyContent: 'space-arround',
            alignItems: 'center'
          },
        },
        {
          props: { variant: 'between-center' },
          style: {
            justifyContent: 'space-between',
            alignItems: 'center'
          },
        },
        {
          props: { variant: 'center-start' },
          style: {
            justifyContent: 'center',
            alignItems: 'start'
          },
        },
        {
          props: { variant: 'start-start' },
          style: {
            justifyContent: 'center',
            alignItems: 'start'
          },
        },
        {
          props: { variant: 'between-start' },
          style: {
            justifyContent: 'space-between',
            alignItems: 'start'
          },
        },
        {
          props: { variant: 'start-end' },
          style: {
            justifyContent: 'start',
            alignItems: 'end'
          },
        },
      ]
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          width: '20px',
          height: '20px',
          position: 'relative'
        },
      },
      variants: [
        {
          props: { variant: 'btn_grey' },
          style: {
            color: color.grey.w500,
            '&:hover': {
              color: color.primary.w600,
              cursor: 'pointer'
            }
          },
        },
        {
          props: { variant: 'lg_48' },
          style: {
            width: '48px',
            height: '48px',
          },
        },
        {
          props: { variant: 'lg_40' },
          style: {
            width: '40px',
            height: '40px',
          },
        },
        {
          props: { variant: 'lg_30' },
          style: {
            width: '30px',
            height: '30px',
          },
        },
        {
          props: { variant: 'md_28' },
          style: {
            width: '28px',
            height: '28px',
          },
        },
        {
          props: { variant: 'md_24' },
          style: {
            width: '24px',
            height: '24px',
          },
        },
        {
          props: { variant: 'md_22' },
          style: {
            width: '22px',
            height: '22px',
          },
        },
        {
          props: { variant: 'sm_18' },
          style: {
            width: '18px',
            height: '18px',
          },
        },
        {
          props: { variant: 'main_28' },
          style: {
            width: '28px',
            height: '28px',
            color: color.primary.w600
          },
        },
        {
          props: { variant: 'tip_center' },
          style: {
            width: '16px',
            height: '16px',
            marginLeft: '2px',
            color: color.grey.w500
          },
        },
        {
          props: { variant: 'form_mr' },
          style: {
            marginRight: '8px',
            verticalAlign: 'sub'
          },
        },
      ],
    },
    MuiButton: {
      defaultProps: {
        disableRipple: true,
        disableElevation: true,
      },
      variants: [
        {
          props: { variant: 'text-cancel' },
          style: {
            marginRight: '8px',
            color: color.grey.w600,
            height: '36px',
            borderRadius: '8px',
          },
        },
        {
          props: { variant: 'text-system-normal' },
          style: {
            color: color.primary.w600,
            '&:hover': {
              backgroundColor: color.primary.w50,
            }
          },
        },
        {
          props: { variant: 'text-grey-normal' },
          style: {
            color: color.text,
            minWidth: 'auto',
            height: '36px',

            '&:hover': {
              color: color.primary.w600,
              backgroundColor: 'transparent'
            }
          },
        },
        {
          props: { variant: 'title-action' },
          style: {
            color: color.blue.w600,
            minWidth: 'auto',
            height: '36px',
            borderRadius: '8px',
            backgroundColor: color.blue.w100,            
            '&:hover': {
              backgroundColor: color.blue.w100,
            }
          },
        },
        {
          props: { variant: 'title' },
          style: {
            color: color.blue.w300,
            minWidth: 'auto',
            height: '36px',
            borderRadius: '8px',
            '&:hover': {
              color: color.blue.w600,
              backgroundColor: color.blue.w100,
            }
          },
        },
        {
          props: { variant: 'fill-main' },
          style: {
            color: '#fff',
            minWidth: 'auto',
            height: '36px',
            backgroundColor: color.primary.w400,
            borderRadius: '8px',

            '&:hover': {
              backgroundColor: color.primary.w800,
            }
          },
        },
        {
          props: { variant: 'fill-grey' },
          style: {
            // color: '#fff',
            minWidth: 'auto',
            height: '36px',
            backgroundColor: color.primary.w50,
            borderRadius: '8px',

            '&:hover': {
              backgroundColor: color.primary.w100,
            }
          },
        },
        {
          props: { variant: 'fill-main-light' },
          style: {
            color: color.primary.w400,
            minWidth: 'auto',
            height: '36px',
            backgroundColor: color.primary.w100,
            borderRadius: '8px',

            '&:hover': {
              backgroundColor: color.primary.w200,
            }
          },
        },
        {
          props: { variant: 'fill-white' },
          style: {
            color: color.text,
            minWidth: 'auto',
            height: '36px',
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow: `0px 2px 4px ${color.grey.w200}`,

            '&:hover': {
              color: '#fff',
              backgroundColor: color.primary.w600,
              boxShadow: `0px 2px 4px ${color.grey.w200}`,
            }
          },
        },
        {
          props: { variant: 'outlined-main' },
          style: {
            color: color.primary.w600,
            minWidth: 'auto',
            height: '36px',
            border: `1px solid ${color.primary.w600}`,
            borderRadius: '8px',

            '&:hover': {
              color: '#fff',
              backgroundColor: color.primary.w600,
            }
          },
        },
        {
          props: { variant: 'outlined-orange' },
          style: {
            color: color.orange.w500,
            minWidth: 'auto',
            height: '36px',
            border: `1px solid ${color.orange.w500}`,
            borderRadius: '8px',

            '&:hover': {
              color: '#fff',
              backgroundColor: color.orange.w500,
            }
          },
        },
        {
          props: { variant: 'outlined-orange-sm' },
          style: {
            color: color.orange.main,
            fontSize: '0.875rem',
            minWidth: 'auto',
            height: '24px',
            padding: '12px 8px',
            border: `1px solid ${color.orange.main}`,
            borderRadius: '8px',

            '&:hover': {
              backgroundColor: color.orange.w0,
            }
          },
        },
        {
          props: { variant: 'outlined-main-sm' },
          style: {
            color: color.primary.w600,
            minWidth: 'auto',
            height: '24px',
            padding: '6px',
            border: `1px solid ${color.primary.w600}`,

            '&:hover': {
              backgroundColor: color.primary.w0,
            }
          },
        },
        {
          props: { variant: 'outlined-grey' },
          style: {
            color: color.grey.w300,
            minWidth: 'auto',
            height: '36px',
            backgroundColor: '#fff',
            border: `1px solid ${color.grey.w300}`,
            borderRadius: '8px',

            '&:hover': {
              color: color.primary.w600,
              border: `1px solid ${color.primary.w600}`,
              backgroundColor: '#fff',
            }
          },
        },
        {
          props: { variant: 'auth-fill' },
          style: {
            height: '36px',
            color: '#fff',
            backgroundColor: '#06C755',
            borderRadius: '8px',

            '& img': {
              width: '20px',
              height: '20px',
              marginRight: '8px'
            },

            '&:hover': {
              backgroundColor: '#05B34C',
            }
          },
        },
      ],
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          '&.MuiButton-contained': {
            color: '#FFF',
            boxShadow: 'none',

            '&:hover': {
              boxShadow: 'none',
            }
          },
          '&.MuiChip-root': {
            height: '22px',
            margin: '1px 2px 1px 0',

            '&.MuiChip-colorPrimary': {
              color: '#fff',

              '& .MuiChip-deleteIcon': {
                width: '16px',
                height: '16px',
                color: 'rgba(255, 255, 255, 0.7)',

                '&:hover': {
                  color: '#fff'
                }
              }
            }
          },
        },
      },
      defaultProps: {
        disableRipple: true,
        // disableElevation: true
      }
    },
    MuiIconButton: {
      variants: [
        {
          props: { variant: 'grey' },
          style: {
            color: color.grey.w600,

            '& .MuiSvgIcon-root': {
              '&:hover': {
                color: color.primary.w600,
              }
            }
          },
        },
        {
          props: { variant: 'primary' },
          style: {
            color: color.red.main,
          },
        },
        {
          props: { variant: 'title' },
          style: {
            color: color.title,

            '& .MuiSvgIcon-root': {
              '&:hover': {
                color: color.primary.w600,
              }
            }
          },
        },
        {
          props: { variant: 'back_to_main' },
          style: {
            color: color.primary.w600,
            '&:hover': {
              color: color.primary.w600,
              backgroundColor: color.primary.w50,
            }
          },
        },
        {
          props: { variant: 'grey-white' },
          style: {
            color: color.grey.w600,
            backgroundColor: '#fff',     
            marginRight: '8px',       
            '&:hover': {
              color: color.primary.w600,
              backgroundColor: '#fff',
            }
          },
        },
      ]
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          minHeight: '400px',
          maxHeight: 'calc(100vh - 250px)',
          borderRadius: '4px',
        }
      }
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          position: 'sticky',
          top: 0,
          zIndex: 5,

          '.MuiTableCell-root': {
            paddingTop: '10px',
            paddingBottom: '10px',
            fontWeight: 500,
          },

          '& .MuiTableSortLabel-icon': {
            position: 'absolute',
            left: '100%',
          },

          '.MuiTableSortLabel-root:hover': {
            color: color.grey.w800
          },

          '.MuiTableSortLabel-root.Mui-active': {
            color: color.grey.w800
          },
        }
      }
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          '&.Mui-active .MuiTableSortLabel-icon': {
          }
        },
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'all .5s',
          backgroundColor: '#fff',

          '.MuiTableCell-root': {
            // paddingTop: '8px',
            // paddingBottom: '8px',
          },

          '&.MuiTableRow-hover:hover': {
            backgroundColor: color.primary.w50,
          },

          '&:last-child td, &:last-child th': {
            // borderBottom: 'none',
          }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
        },
      }
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          height: '24px',
          // height: '40px',
          lineHeight: '40px',
        },
        select: {
          '&.MuiInputBase-input.MuiSelect-select': {
            backgroundColor: 'transparent'
          }
        },
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-input': {

            '&.Mui-disabled': {
              WebkitTextFillColor: color.text,
              borderRadius: '8px',
              backgroundColor: 'none',
            },
          },
        }
      }
    },
    MuiTextField: {
      defaultProps: {
        size: 'small'
      },
      styleOverrides: {
        root: {
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          width: '100%',
          borderRadius: '6px',
          '& .MuiInputBase-input': {
            height: '40px',
            padding: '0 10px',
            borderColor: color.grey.w300,
          },
          '& .MuiOutlinedInput-notchedOutline': {
            transition: 'border .5s',
          },
          '&:hover': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: color.primary.w400,
              borderWidth: '1px',
            }
          },
          '&.Mui-focused': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: color.primary.w600,
              borderWidth: '1px',
            }
          },
        },
        notchedOutline: {
          borderColor: color.grey.w300,
        },
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
            width: '100%',
            padding: '8px',

            '& .MuiAutocomplete-input': {
              height: '24px',
              padding: 0
            }
          },

          '& .MuiChip-root': {
            height: '22px',
            margin: '1px 2px 1px 0',
            backgroundColor: color.primary.w600,
            color: '#fff',

            '& .MuiChip-deleteIcon': {
              width: '16px',
              height: '16px',
              color: 'rgba(255, 255, 255, 0.7)',

              '&:hover': {
                color: '#fff'
              }
            }
          },
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
        },
      },
      variants: [
        {
          props: { variant: 'rounded-rect' },
          style: {
            padding: '10px 5px',
            fontSize: '0.875rem',
            backgroundColor: color.primary.w400,
          },
        },
        {
          props: { variant: 'outline-white' },
          style: {
            color: '#fff',
            fontSize: '0.875rem',
            border: `1px solid #fff`,
            backgroundColor: 'none',
          },
        },
        {
          props: { variant: 'outline-grey' },
          style: {
            color: color.grey.w500,
            fontSize: '0.875rem',
            border: `1px solid ${color.grey.w500}`,
            background: 'none',
          },
        },
        {
          props: { variant: 'outline-main' },
          style: {
            color: color.primary.w600,
            fontSize: '0.875rem',
            border: `1px solid  ${color.primary.w600}`,
            backgroundColor: 'none',
          },
        },
        {
          props: { variant: 'text-grey' },
          style: {
            padding: '10px 5px',
            color: color.grey.w600,
          },
        },
        {
          props: { variant: 'rounded-orange' },
          style: {
            padding: '10px 5px',
            color: color.orange.main,
            backgroundColor: color.orange.light,
          },
        },
        {
          props: { variant: 'rounded-blue' },
          style: {
            padding: '10px 5px',
            color: color.primary.w600,
            backgroundColor: color.primary.w0,
          },
        },
        {
          props: { variant: 'rounded-purple' },
          style: {
            padding: '10px 5px',
            color: color.purple.main,
            backgroundColor: color.purple.light,
          },
        },
        {
          props: { variant: 'rounded-lightblue' },
          style: {
            padding: '10px 5px',
            color: color.lightblue.main,
            backgroundColor: color.lightblue.light,
          },
        },
      ],
    },
    MuiModal: {
      styleOverrides: {
        root: {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }
      }
    },
    MuiSlider: {
      styleOverrides: {
        root: {
        },
        thumb: {
          backgroundColor: color.primary.w600,
          '&:before': {
            boxShadow: `0px 2px 6px ${color.grey.w300}`,
          },
        },
        track: {
          backgroundColor: color.primary.w500,
        },
        rail: {
          backgroundColor: color.primary.w500,
        },
        mark: {
          backgroundColor: '#bfbfbf',
          height: 8,
          width: 1,
          '&.MuiSlider-markActive': {
            opacity: 1,
            backgroundColor: 'currentColor',
          },
        }
      }
    },
  }

});

export default theme;